import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";
import { AuthHero } from "@/components/AuthHero";

const dest = (role) => (["platform_owner", "admin"].includes(role) ? "/command" : "/app");

const INPUT = "mt-1.5 w-full rounded-xl border border-white/10 bg-[#111a2e] px-4 py-3 text-white outline-none transition-colors placeholder:text-slate-500 focus:border-[#FF7A00]";
const LABEL = "block text-xs font-semibold uppercase tracking-wide text-slate-400";
const BTN = "w-full rounded-full bg-[#FF7A00] py-3.5 font-semibold text-white transition-transform hover:scale-[1.02] disabled:opacity-60";

export default function Login() {
  const { login, applyAuth, error, setError } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawNext = params.get("next") || "";
  const nextUrl = /^\/(?!\/)/.test(rawNext) ? rawNext : "";
  const go = (role) => navigate(nextUrl && !["platform_owner", "admin"].includes(role) ? nextUrl : dest(role));
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [otpMode, setOtpMode] = useState("off");
  const [stage, setStage] = useState("creds");
  const [challenge, setChallenge] = useState(null);
  const [channel, setChannel] = useState("email");
  const [hint, setHint] = useState("");
  const [code, setCode] = useState("");
  const [smsLogin, setSmsLogin] = useState(false);
  const [trustDevice, setTrustDevice] = useState(false);

  const passwordless = otpMode === "passwordless";

  useEffect(() => {
    api.get("/settings/public").then(({ data }) => setOtpMode(data.otp_mode)).catch(() => {});
  }, []);

  const submitCreds = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      if (smsLogin) {
        const { data } = await api.post("/auth/login/sms/start", { identifier: email, device_token: localStorage.getItem("iyeneh_device_token") || undefined });
        if (data.trusted) { const u = applyAuth(data); go(u.role); return; }
        if (data.challenge_id) { setChallenge(data.challenge_id); setChannel("sms"); setHint(data.hint || email); setStage("otp"); }
        else { toast.success("If that account has a verified number, we've texted a code."); }
      } else if (passwordless) {
        const { data } = await api.post("/auth/otp/start", { email, channel: "email" });
        if (data.challenge_id) {
          setChallenge(data.challenge_id); setChannel(data.channel); setHint(data.hint || email);
          setStage("otp");
        } else {
          toast.success("If that account exists, a sign-in code has been sent.");
        }
      } else {
        const res = await login(email, password);
        if (res && res.requires_otp) {
          setChallenge(res.challenge_id); setChannel(res.channel); setHint(res.hint || "");
          setStage("otp");
        } else if (res) {
          go(res.role);
        }
      }
    } catch (_) {} finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/otp/verify", { challenge_id: challenge, code: code.trim(), trust_device: (smsLogin && trustDevice) || undefined });
      if (data.device_token) localStorage.setItem("iyeneh_device_token", data.device_token);
      const u = applyAuth(data);
      go(u.role);
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true); setError("");
    try {
      if (smsLogin) {
        const { data } = await api.post("/auth/login/sms/start", { identifier: email });
        if (data.challenge_id) { setChallenge(data.challenge_id); toast.success("New code sent"); }
      } else if (passwordless) {
        const { data } = await api.post("/auth/otp/start", { email, channel });
        if (data.challenge_id) { setChallenge(data.challenge_id); toast.success("New code sent"); }
      } else {
        const res = await login(email, password);
        if (res && res.requires_otp) { setChallenge(res.challenge_id); toast.success("New code sent"); }
      }
    } catch (_) {} finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen bg-[#070B16] text-white md:grid-cols-2">
      <AuthHero />

      <div className="flex items-center justify-center bg-[#070B16] p-6 text-white">
        {stage === "creds" ? (
          <form onSubmit={submitCreds} className="w-full max-w-sm animate-fade-up" data-testid="login-creds">
            <h1 className="font-display text-3xl font-semibold tracking-tight">Sign in</h1>
            <p className="mt-2 text-sm text-slate-400">{smsLogin ? "Enter your email or mobile number and we'll text you a sign-in code." : passwordless ? "Enter your email and we'll send you a sign-in code." : "Access your universal wallet and connections."}</p>

            {error && <div data-testid="login-error" className="mt-4 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</div>}

            <label className="mt-6 block text-xs font-semibold uppercase tracking-wide text-slate-400">{smsLogin ? "Email or mobile number" : "Email"}</label>
            <input data-testid="login-email" type={smsLogin ? "text" : "email"} required value={email} onChange={(e) => setEmail(e.target.value)}
              className={INPUT} placeholder={smsLogin ? "you@company.com or +1202…" : "you@company.com"} />

            {!passwordless && !smsLogin && (
              <>
                <div className="mt-4 flex items-center justify-between">
                  <label className={LABEL}>Password</label>
                  <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-[#FF7A00]">Forgot password?</Link>
                </div>
                <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  className={INPUT} placeholder="••••••••" />
              </>
            )}

            <button data-testid="login-submit" type="submit" disabled={busy} className={`mt-6 ${BTN}`}>
              {busy ? "Please wait…" : smsLogin ? "Text me a code" : passwordless ? "Send code" : "Sign in"}
            </button>

            {!passwordless && (
              <button type="button" data-testid="toggle-sms-login" onClick={() => { setSmsLogin((v) => !v); setError(""); }} className="mt-3 w-full text-center text-sm text-[#FF7A00]">
                {smsLogin ? "Use password instead" : "Sign in with a text code"}
              </button>
            )}

            <p className="mt-6 text-center text-sm text-slate-400">
              New here? <Link to="/register" data-testid="to-register" className="font-semibold text-[#FF7A00]">Create an account</Link>
            </p>
            <div className="mt-4 rounded-lg bg-white/5 px-3 py-2 text-center text-xs text-slate-400">
              Demo: demo@iyeneh.io · Demo#2026
            </div>
            <p className="mt-4 text-center text-[11px] text-slate-500" data-testid="login-legal">
              By continuing you agree to our <Link to="/terms" className="text-slate-400 underline hover:text-white">Terms</Link> and <Link to="/privacy" className="text-slate-400 underline hover:text-white">Privacy Policy</Link>.
            </p>
          </form>
        ) : (
          <form onSubmit={submitOtp} className="w-full max-w-sm animate-fade-up" data-testid="login-otp">
            <button type="button" onClick={() => { setStage("creds"); setCode(""); setError(""); }} className="mb-4 flex items-center gap-1 text-sm text-slate-400"><ArrowLeft size={14} /> Back</button>
            <h1 className="font-display text-3xl font-semibold tracking-tight">Enter your code</h1>
            <p className="mt-2 text-sm text-slate-400">We sent a verification code to {channel === "sms" ? "your phone" : "your email"} <span className="font-medium text-white">{hint}</span>. It expires in 5 minutes.</p>

            {error && <div data-testid="otp-error" className="mt-4 rounded-lg bg-red-500/10 px-3 py-2 text-sm text-red-400">{error}</div>}

            <input data-testid="otp-code" inputMode="numeric" maxLength={10} autoFocus value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="mt-6 w-full rounded-xl border border-white/10 bg-[#111a2e] px-4 py-3 text-center text-2xl font-semibold tracking-[0.5em] text-white outline-none focus:border-[#FF7A00]" placeholder="••••••" />

            <button data-testid="otp-verify" type="submit" disabled={busy || !code.trim()} className={`mt-6 ${BTN}`}>
              {busy ? "Verifying…" : "Verify & sign in"}
            </button>
            {smsLogin && channel === "sms" && (
              <label className="mt-4 flex items-center gap-2 text-sm text-slate-400">
                <input type="checkbox" data-testid="trust-device" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} className="h-4 w-4 rounded border-white/20 bg-[#111a2e]" />
                Trust this device for 30 days (skip the code next time)
              </label>
            )}
            <button type="button" data-testid="otp-resend" onClick={resend} disabled={busy} className="mt-4 w-full text-center text-sm text-[#FF7A00]">Resend code</button>
          </form>
        )}
      </div>
    </div>
  );
}
